import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "sonner";
import { trackCorporatePageView } from "./lib/corporateGa4";

// Public
import Home from "./pages/public/Home";
import Platforms from "./pages/public/Platforms";
import PlatformComingSoon from "./pages/public/PlatformComingSoon";
import HotJobs, { HotJobDetail } from "./pages/public/HotJobs";
import NewGradNurses from "./pages/public/NewGradNurses";
import { VerifiedJobsIndex, VerifiedJobDetail } from "./pages/public/VerifiedJobs";
import Clinicians from "./pages/public/Clinicians";
import Employers from "./pages/public/Employers";
import Services from "./pages/public/Services";
import ServiceDetail from "./pages/public/ServiceDetail";
import Compliance from "./pages/public/Compliance";
import ChecklistHub from "./pages/public/ChecklistHub";
import ChecklistDetail from "./pages/public/ChecklistDetail";
import ChecklistForm from "./pages/public/ChecklistForm";
import NurseChecklist from "./pages/public/NurseChecklist";
import TherapistChecklist from "./pages/public/TherapistChecklist";
import OtherChecklist from "./pages/public/OtherChecklist";
import MyChecklists from "./pages/public/MyChecklists";
import SharedChecklist from "./pages/public/SharedChecklist";
import ScanChecklist from "./pages/public/ScanChecklist";
import Diversity from "./pages/public/Diversity";
import CapabilityStatement from "./pages/public/CapabilityStatement";
import VerifyCapability from "./pages/public/VerifyCapability";
import MobileTextRecruiterFAB from "./components/MobileTextRecruiterFAB";
import Blog from "./pages/public/Blog";
import BlogPost from "./pages/public/BlogPost";
import About from "./pages/public/About";

// TRITAL ShiftBridge™ — PRN marketplace
import ShiftsHome from "./pages/shifts/ShiftsHome";
import ForFacilities from "./pages/shifts/ForFacilities";
import ForClinicians from "./pages/shifts/ForClinicians";
import BrowseShifts from "./pages/shifts/BrowseShifts";
import FacilitySignup from "./pages/shifts/FacilitySignup";
import ClinicianSignup from "./pages/shifts/ClinicianSignup";
import ShiftsLogin from "./pages/shifts/ShiftsLogin";
import ShiftsFAQ from "./pages/shifts/ShiftsFAQ";
import ShiftsContact from "./pages/shifts/ShiftsContact";
import ShiftsDashboard from "./pages/shifts/ShiftsDashboard";
import SeoHub from "./pages/shifts/SeoHub";
import SeoLanding from "./pages/shifts/SeoLanding";
import Careers from "./pages/public/Careers";
import CareersJobs from "./pages/public/CareersJobs";
import Contact from "./pages/public/Contact";
import Terms from "./pages/public/Terms";
import PrivacyPolicy from "./pages/public/PrivacyPolicy";
import LegalPlaceholder from "./pages/public/LegalPlaceholder";

// TRITAL Workforce OS™ — Free AI-Ready healthcare recruiting tools
import WorkforceOsHome from "./pages/public/workforce_os/WorkforceOsHome";
import AiNurseRecruiter from "./pages/public/workforce_os/AiNurseRecruiter";
import ResumeFormatter from "./pages/public/workforce_os/ResumeFormatter";
import RecruiterOsRateLab from "./pages/public/workforce_os/RecruiterOsRateLab";
import RateLabV9Experience from "./pages/public/workforce_os/RateLabV9Experience";
import RateLabShare from "./pages/public/workforce_os/RateLabShare";
import SubmittalScore from "./pages/public/workforce_os/SubmittalScore";
import MspReadiness from "./pages/public/workforce_os/MspReadiness";
import ClinicianPassport from "./pages/public/workforce_os/ClinicianPassport";
import WorkforceOsToolPlaceholder from "./pages/public/workforce_os/WorkforceOsToolPlaceholder";
import RebecaFloatingMount from "./components/workforce_os/RebecaFloatingMount";

// Auth
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";

// Dashboard pages
import DashboardHome from "./pages/dashboard/DashboardHome";
import ShiftMarketplace from "./pages/dashboard/ShiftMarketplace";
import JobMarketplace from "./pages/dashboard/JobMarketplace";
import TravelCalculator from "./pages/dashboard/TravelCalculator";
import Credentials from "./pages/dashboard/Credentials";
import Skills from "./pages/dashboard/Skills";
import Messages from "./pages/dashboard/Messages";
import Profile from "./pages/dashboard/Profile";
import PostShift from "./pages/dashboard/PostShift";
import PostJob from "./pages/dashboard/PostJob";
import ManageShifts from "./pages/dashboard/ManageShifts";
import ClinicianDirectory from "./pages/dashboard/ClinicianDirectory";
import RecruiterPipeline from "./pages/dashboard/RecruiterPipeline";
import FacilityOperations from "./pages/dashboard/FacilityOperations";
import AdminPanel from "./pages/dashboard/AdminPanel";
import DashboardLayout from "./components/layout/DashboardLayout";

function DashRoot() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard/home" replace />;
}

/** Scrolls to hash anchor when URL changes, and to top on plain navigation.
 *  Retries across animation frames (up to ~1.2s) + also a late setTimeout so
 *  cross-page hash links (e.g. mega-menu / -> /employers#payroll) land on the
 *  correct card even when the destination component commits after React Router
 *  has already fired this effect. */
function ScrollManager() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      // Only treat hash as an anchor id if it matches simple CSS-safe id syntax.
      // Otherwise (e.g. `#d=...&c=...` from shareable-link payloads) skip the
      // anchor-scroll entirely — the route component owns the hash.
      const isAnchor = /^#[A-Za-z][\w\-:.]*$/.test(hash);
      if (!isAnchor) return;
      let frames = 0;
      const MAX_FRAMES = 72; // ~1.2s at 60fps
      const scroll = () => {
        let el = null;
        try {
          el = document.querySelector(hash);
        } catch {
          return true; // invalid selector — give up silently
        }
        if (el) {
          // Offset for sticky header (72 px) + a little breathing room.
          const y = el.getBoundingClientRect().top + window.scrollY - 88;
          window.scrollTo({ top: y, behavior: "smooth" });
          return true;
        }
        return false;
      };
      const raf = () => {
        if (scroll()) return;
        if (frames++ < MAX_FRAMES) requestAnimationFrame(raf);
      };
      raf();
      // Last-resort safety net if the component mounts later than MAX_FRAMES.
      const t = setTimeout(scroll, 1400);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash]);
  return null;
}

/** Track sanitized public page views on corporate hosts only. No private routes. */
function CorporatePageAnalytics() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const frame = window.requestAnimationFrame(() => trackCorporatePageView(pathname));
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollManager />
          <CorporatePageAnalytics />
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Public */}
            {/* Temporary host-aware landing until ShiftBridge receives its dedicated frontend deployment. */}
            <Route path="/" element={typeof window !== "undefined" && window.location.hostname.toLowerCase() === "shifts.tritalcare.com" ? <ShiftsHome /> : <Home />} />
            <Route path="/platforms" element={<Platforms />} />
            <Route path="/platforms/:platform" element={<PlatformComingSoon />} />
            <Route path="/jobs" element={<VerifiedJobsIndex />} />
            <Route path="/jobs/:slug" element={<VerifiedJobDetail />} />
            <Route path="/hot-jobs" element={<HotJobs />} />
            <Route path="/hot-jobs/new-grad-nurses" element={<NewGradNurses />} />
            <Route path="/hot-jobs/:slug" element={<HotJobDetail />} />
            <Route path="/employers" element={<Employers />} />
            <Route path="/solutions" element={<Navigate to="/employers" replace />} />
            <Route path="/clinicians" element={<Clinicians />} />

            {/* Services hub + children */}
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />

            {/* Compliance hub + checklists hub + children */}
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/compliance/checklists" element={<ChecklistHub />} />
            <Route path="/compliance/checklists/nurse" element={<NurseChecklist />} />
            <Route path="/compliance/checklists/therapist" element={<TherapistChecklist />} />
            <Route path="/compliance/checklists/other" element={<OtherChecklist />} />
            {/* Dynamic per-checklist detail form */}
            <Route path="/compliance/checklists/nurse/:slug" element={<ChecklistForm />} />
            <Route path="/compliance/checklists/therapist/:slug" element={<ChecklistForm />} />
            <Route path="/compliance/checklists/other/:slug" element={<ChecklistForm />} />
            {/* Legacy single-segment slug fallback */}
            <Route path="/compliance/checklists/:slug" element={<ChecklistDetail />} />

            {/* My Checklists — unified clinician aggregate */}
            <Route path="/my-checklists" element={<MyChecklists />} />

            {/* Shareable checklist URL — decodes payload from URL fragment */}
            <Route path="/c" element={<SharedChecklist />} />
            {/* Recruiter QR scanner */}
            <Route path="/c/scan" element={<ScanChecklist />} />

            {/* Legacy alias: /checklists → hub */}
            <Route path="/checklists" element={<Navigate to="/compliance/checklists" replace />} />
            <Route path="/checklists/:slug" element={<Navigate to="/compliance/checklists" replace />} />

            {/* Standalone public */}
            <Route path="/diversity-and-inclusion" element={<Diversity />} />
            {/* Legacy alias */}
            <Route path="/diversity" element={<Navigate to="/diversity-and-inclusion" replace />} />

            {/* Capability Statement — live personalization + QR-verified snapshot */}
            <Route path="/capability-statement" element={<CapabilityStatement />} />
            <Route path="/verify" element={<VerifyCapability />} />

            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/jobs" element={<CareersJobs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/cookies" element={<LegalPlaceholder title="Cookie Notice" />} />
            <Route path="/legal/accessibility" element={<LegalPlaceholder title="Accessibility Statement" />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            {/* TRITAL Workforce OS™ — Free AI-Ready Healthcare Recruiting Tools */}
            <Route path="/workforce-os" element={<WorkforceOsHome />} />
            <Route path="/workforce-os/ai-nurse-recruiter" element={<AiNurseRecruiter />} />
            <Route path="/workforce-os/resume-formatter" element={<ResumeFormatter />} />
            <Route path="/workforce-os/resume-library" element={<ResumeFormatter />} />
            <Route path="/workforce-os/ratelab" element={<RateLabV9Experience />} />
            <Route path="/workforce-os/ratelab-v9-preview" element={<Navigate to="/workforce-os/ratelab" replace />} />
            <Route path="/workforce-os/submittal-score" element={<SubmittalScore />} />
            <Route path="/workforce-os/msp-readiness" element={<MspReadiness />} />
            <Route path="/workforce-os/clinician-passport" element={<ClinicianPassport />} />
            {/* All remaining tool routes use the unified placeholder driven by TOOL_BY_SLUG */}
            <Route path="/workforce-os/:slug" element={<WorkforceOsToolPlaceholder />} />

            {/* TRITAL Recruiter OS™ — Production RateLab Travel W2 Calculator */}
            <Route path="/recruiter-os/ratelab" element={<Navigate to="/workforce-os/ratelab" replace />} />
            <Route path="/recruiter-os/ratelab-legacy" element={<RecruiterOsRateLab />} />
            <Route path="/ratelab/share/:shareId" element={<RateLabShare />} />
            <Route path="/recruiter-os" element={<Navigate to="/workforce-os" replace />} />

            {/* TRITAL ShiftBridge™ (legacy teaser → marketplace) */}
            <Route path="/shift-connect" element={<Navigate to="/shiftbridge" replace />} />
            <Route path="/shifts" element={<Navigate to="/shiftbridge" replace />} />
            <Route path="/prn" element={<Navigate to="/shiftbridge" replace />} />

            {/* PRN Marketplace — TRITAL ShiftBridge™ */}
            <Route path="/shiftbridge" element={<ShiftsHome />} />
            <Route path="/shiftbridge/facilities" element={<ForFacilities />} />
            <Route path="/shiftbridge/clinicians" element={<ForClinicians />} />
            <Route path="/shiftbridge/browse" element={<BrowseShifts />} />
            <Route path="/shiftbridge/signup/facility" element={<FacilitySignup />} />
            <Route path="/shiftbridge/signup/clinician" element={<ClinicianSignup />} />
            <Route path="/shiftbridge/login" element={<ShiftsLogin />} />
            <Route path="/shiftbridge/faq" element={<ShiftsFAQ />} />
            <Route path="/shiftbridge/contact" element={<ShiftsContact />} />
            <Route path="/shiftbridge/dashboard" element={<ShiftsDashboard />} />

            {/* Programmatic SEO landing pages — ~338 long-tail PRN URLs */}
            <Route path="/shiftbridge/prn-shifts" element={<SeoHub />} />
            <Route path="/shiftbridge/prn-shifts/:slug" element={<SeoLanding />} />

            {/* Legal placeholders */}
            <Route path="/legal/cookies" element={<LegalPlaceholder title="Cookie Notice" />} />
            <Route path="/legal/accessibility" element={<LegalPlaceholder title="Accessibility Statement" />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Dashboard (protected) */}
            <Route path="/dashboard" element={<ProtectedRoute><DashRoot /></ProtectedRoute>} />
            <Route path="/dashboard/*" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="home" element={<DashboardHome />} />
              <Route path="shifts" element={<ShiftMarketplace />} />
              <Route path="jobs" element={<JobMarketplace />} />
              <Route path="calculator" element={<TravelCalculator />} />
              <Route path="credentials" element={<Credentials />} />
              <Route path="skills" element={<Skills />} />
              <Route path="messages" element={<Messages />} />
              <Route path="profile" element={<Profile />} />
              <Route path="post-shift" element={<ProtectedRoute roles={["facility","admin"]}><PostShift /></ProtectedRoute>} />
              <Route path="post-job" element={<ProtectedRoute roles={["facility","recruiter","admin"]}><PostJob /></ProtectedRoute>} />
              <Route path="manage-shifts" element={<ProtectedRoute roles={["facility","admin"]}><ManageShifts /></ProtectedRoute>} />
              <Route path="facility" element={<ProtectedRoute roles={["facility","admin"]}><FacilityOperations /></ProtectedRoute>} />
              <Route path="recruiter" element={<ProtectedRoute roles={["recruiter","admin"]}><RecruiterPipeline /></ProtectedRoute>} />
              <Route path="directory" element={<ProtectedRoute roles={["facility","recruiter","admin"]}><ClinicianDirectory /></ProtectedRoute>} />
              <Route path="admin" element={<ProtectedRoute roles={["admin"]}><AdminPanel /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <MobileTextRecruiterFAB />
          <RebecaFloatingMount />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;

